import React from 'react';
import { LinkPropsType, toSimpleLink } from '../../../lib/helpers/index';
import {
	EventCategory,
	InvoiceSentOrResentByEventPropsEventFragment,
	UserType,
} from '#types';
import { gql } from '@apollo/client';
import rpcShared from '@rockpapercoin/rpc-shared';
import { Typography } from '@mui/material';
import { FlattenedUser } from '../../../types/user';
import { DefaultEventProps } from '../contract/helpers';

export const invoiceSentOrResentByEventPropsEventEventFragment = gql`
	fragment invoiceSentOrResentByEventPropsEvent on Event {
		id
		category
		actingUser {
			id
		}
		resolvedAt
		invoice {
			id
			title
			organization {
				id
			}
		}
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
				clientUser {
					id
					plannerOrgCanPayInvoices
					assignedPlanner {
						id
						organization {
							id
						}
					}
				}
			}
		}
		vendor {
			id
			name
		}
	}
`;

type SimpleLinkProps = {
	text: string;
	link: LinkPropsType;
};

export const getInvoiceSentOrResentByEventProps = (
	defaultEventProps: DefaultEventProps & {
		event: InvoiceSentOrResentByEventPropsEventFragment;
	},
	user: FlattenedUser
) => {
	const { event } = defaultEventProps;
	if ( !event.invoice || !event.contact || !event.vendor ) {
		throw new Error( `Event ID ${ event.id } missing required data` );
	}

	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	const { invoice, contact, category, vendor, resolvedAt, actingUser } = event;

	const invoiceLinkProps: SimpleLinkProps = {
		text: invoice.title,
		link: {
			href: `/invoice/${ invoice.id }`,
		},
	};
	const invoiceTitleLink = toSimpleLink( {
		...invoiceLinkProps,
		text: invoice.title,
	} );
	const invoiceTextLink = toSimpleLink( {
		...invoiceLinkProps,
		text: 'invoice',
	} );
	const vendorLink = toSimpleLink( {
		text: vendor.name,
		link: {
			href: `/org/${ vendor.id }`,
		},
	} );
	const customerName = rpcShared.contacts.getCustomerName( contact ).fullName;
	const customerLink = toSimpleLink( {
		text: customerName,
		link: {
			href: `/client/${ contact.customer.id }`,
		},
	} );
	const viewerIsCustomer = user?.isLoggedIn
		? rpcShared.invoices.isCustomer( invoice, user.user.id )
		: false;
	const viewerIsAPPA = orgUser
		? rpcShared.contacts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
			contact,
			orgUser
		  )
		: false;

	defaultEventProps.icon = 'invoice';
	defaultEventProps.links = [
		{
			type: 'secondary',
			text: 'View',
			link: invoiceLinkProps.link,
		},
	];

	if ( viewerIsCustomer ) {
		const message =
			category === EventCategory.InvoiceResent ? (
				<Typography>
					{ invoiceTitleLink } has been resent by { vendorLink }
				</Typography>
			) : (
				<Typography>
					New { invoiceTextLink } from { vendorLink }
				</Typography>
			);
		return {
			...defaultEventProps,
			message,
			links: [
				{
					type: resolvedAt ? 'secondary' : 'primary',
					text: resolvedAt ? 'View' : 'Pay',
					link: invoiceLinkProps.link,
				},
			],
		};
	} else if ( viewerIsAPPA ) {
		const hasPayingPermission =
			contact.customer.clientUser?.plannerOrgCanPayInvoices &&
			orgUser &&
			invoice.organization?.id !== orgUser.organization.id;
		const message =
			category === EventCategory.InvoiceResent ? (
				<Typography>
					{ invoiceTitleLink } has been resent by { vendorLink }
				</Typography>
			) : (
				<Typography>
					New { invoiceTextLink } from { vendorLink }
				</Typography>
			);
		return {
			...defaultEventProps,
			message,
			links: [
				{
					type: resolvedAt || !hasPayingPermission ? 'secondary' : 'primary',
					text: resolvedAt || !hasPayingPermission ? 'View' : 'Pay',
					link: invoiceLinkProps.link,
				},
			],
		};
	} else if ( orgUser && orgUser.user.id === actingUser?.id ) {
		// Is in org that sent invoice
		const message =
			category === EventCategory.InvoiceResent ? (
				<Typography>
					You resent { invoiceTitleLink } to { customerLink }
				</Typography>
			) : (
				<Typography>
					You sent { customerLink } an invoice: { invoiceTitleLink }
				</Typography>
			);
		return {
			...defaultEventProps,
			message,
		};
	} else if ( orgUser && orgUser.organization.id === invoice.organization?.id ) {
		// Is in org that sent invoice
		const message =
			category === EventCategory.InvoiceResent ? (
				<Typography>
					Your organization resent { customerLink } an invoice: { invoiceTextLink }
				</Typography>
			) : (
				<Typography>
					Your organization sent { customerLink } an invoice: { invoiceTextLink }
				</Typography>
			);
		return {
			...defaultEventProps,
			message,
		};
	} else {
		// not customer nor APPA not user from sending org, but treating like a customer
		const message =
			category === EventCategory.InvoiceResent ? (
				<Typography>
					{ invoiceTitleLink } has been resent by { vendorLink }
				</Typography>
			) : (
				<Typography>
					New { invoiceTextLink } from { vendorLink }
				</Typography>
			);
		return {
			...defaultEventProps,
			message,
			links: [
				{
					type: resolvedAt ? 'secondary' : 'primary',
					text: resolvedAt ? 'View' : 'Pay',
					link: invoiceLinkProps.link,
				},
			],
		};
	}
};
